import React, { useState, useMemo } from 'react';

import { preAlertApis } from '@apis/next/preAlertApis';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { Layout } from '@components/Layout';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { cellFactory, Table, TableCellPropsUnion } from '@components/Table/Table';
import { BookingsContainer } from '@components/utilities/Bookings/BookingsContainer';
import { Filter } from '@components/utilities/Bookings/Filter';
import { bookingsStrings } from '@data/bookings';
import { useSearch } from '@hooks/next/useSearch';
import { PreAlertSearch } from '@models/next/PreAlert';
import { PreAlertTypeType } from '@models/PreAlert';
import { useCommonStyles, makeMuiStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate, formatPrice } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Link, useLocation } from 'react-router-dom';

const useStyles = makeMuiStyles((theme) => {
  return {
    link: {
      color: theme.palette.primary.main,
      whiteSpace: 'pre',
    },
  };
});

const strings = createStrings({
  ja: {
    booking: '予約',
    d2dNo: 'D2D番号',
    author: '作成者',
    nameOfVessel: '船名',
    search: '絞り込み',
    no: 'No',
    createdAt: '作成時間',
    fcManager: 'FC担当者',
    type: '種類',
    status: 'ステータス',
    deliveryDate: '纳品日',
    shipper: '荷送人',
    vessel: '本船名',
    voyage: 'VOY番号',
    from: '輸出港',
    etd: '出港予定日',
    to: '輸入港',
    eta: '入港予定日',
    quantity: '個数',
    grossWeight: '重量',
    measurement: '体積',
    details: '詳細',
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
    customerNo: 'お客様の管理番号',
    reset: 'リセット',
    exportCSV: 'Export CSV',
    commodity: '品名',
    invociePrice: '請求金額',
  },
});

export interface BookingsProps {
  type: 'current' | 'past';
}

export const Bookings: React.FC<BookingsProps> = (props) => {
  const { type } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isMobile = useIsMobile();

  const location = useLocation();

  const { search, updateSearch } = useSearch(PreAlertSearch);

  const { checklist = [], ...preAlertQuery } = search;

  const queries = {
    ...preAlertQuery,
    d2d_no: search.d2d_no && `Y-${search.d2d_no}`,
    type: (type === 'current'
      ? ['current']
      : ['past_canceled', 'past_finished']) satisfies PreAlertTypeType[],
  };

  const preAlert = preAlertApis.useGet('/pre-alert', {
    queries,
  });

  const [UIState, setUIState] = useState(() => ({
    exportDialog: {
      open: false,
    },
  }));

  const preAlerts = preAlert.data?.results ?? [];

  const itemIds = preAlerts.map((item) => item.d2d_no);

  const allChecked =
    preAlerts.length > 0 && preAlerts.every((item) => checklist.includes(item.d2d_no));

  const onChecklistChange = (next: string[]) => {
    updateSearch({ checklist: next });
  };

  const onHeadCheckableChange = () => {
    if (allChecked) {
      onChecklistChange(checklist.filter((id) => !itemIds.includes(id)));
    } else {
      onChecklistChange([...checklist, ...itemIds]);
    }
  };

  const onBodyCheckableChange = (d2d_no: string) => () => {
    if (checklist.includes(d2d_no)) {
      onChecklistChange(checklist.filter((checked) => checked !== d2d_no));
    } else {
      onChecklistChange([...checklist, d2d_no]);
    }
  };

  const pageCount = 10;

  const headColumns: TableCellPropsUnion[] = useMemo(
    () => [
      cellFactory.createCheckable(allChecked, onHeadCheckableChange),
      cellFactory.create(strings.no),
      cellFactory.create(strings.d2dNo),
      cellFactory.createBlank(),
      cellFactory.create(strings.from),
      cellFactory.create(strings.etd),
      cellFactory.create(strings.to),
      cellFactory.create(strings.eta),
      cellFactory.create(strings.vessel),
      cellFactory.create(strings.voyage),
      cellFactory.create(strings.commodity),
      cellFactory.create(strings.quantity),
      cellFactory.create(strings.grossWeight),

      cellFactory.create(strings.measurement),
      cellFactory.create(strings.customerNo),
      type === 'current'
        ? cellFactory.create(strings.status)
        : cellFactory.create(strings.deliveryDate),
      ...(type === 'past' ? [cellFactory.create(strings.invociePrice)] : []),
    ],
    [allChecked, location],
  );
  // eslint-disable-next-line max-len
  const bodyRows: TableCellPropsUnion[][] | undefined = preAlert.data?.results.map((item, i) => {
    return [
      cellFactory.createCheckable(
        checklist.includes(item.d2d_no),
        onBodyCheckableChange(item.d2d_no),
      ),
      cellFactory.create(i + pageCount * ((search.page ?? 1) - 1) + 1),
      cellFactory.create(<span className={classes.whitespacePre}>{item.d2d_no}</span>),
      cellFactory.create(
        <Link
          className={classes.link}
          to={`${
            type === 'current'
              ? url('/utilities/Bookings/CurrentBookingDetails')
              : url('/utilities/Bookings/PastBookingDetails')
          }?d2dNo=${item.d2d_no}`}
        >
          {strings.details}
        </Link>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>{item.is_empty ? '-' : item.pol || ''}</span>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>
          {item.is_empty ? '-' : formatDate(item.etd) || '-'}
        </span>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>{item.is_empty ? '-' : item.pod || '-'}</span>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>
          {item.is_empty ? '-' : formatDate(item.eta) || '-'}
        </span>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>{item.is_empty ? '-' : item.vessel || '-'}</span>,
      ),
      cellFactory.create(
        <span className={classes.whitespacePre}>{item.is_empty ? '-' : item.voyage || '-'}</span>,
      ),
      cellFactory.create(<span className={classes.whitespacePre}>{item.commodity || '-'}</span>),

      cellFactory.create(<span className={classes.whitespacePre}>{item.quantity || '-'}</span>),
      cellFactory.create(<span className={classes.whitespacePre}>{item.gross_weight || '-'}</span>),
      cellFactory.create(<span className={classes.whitespacePre}>{item.measurement || '-'}</span>),

      cellFactory.create(<span className={classes.whitespacePre}>{item.customer_no || '-'}</span>),
      cellFactory.create(
        <>
          {type === 'current' && (
            <span className={classes.whitespacePre}>{item.transport_status || '-'}</span>
          )}
          {type === 'past' && (
            <span className={classes.whitespacePre}>
              {item.delivery_date
                ? dayjs(item.delivery_date).tz('Asia/Tokyo').format('YYYY-MM-DD')
                : '-'}
            </span>
          )}
        </>,
      ),
      ...(type === 'past'
        ? [
            cellFactory.create(
              <span className={classes.whitespacePre}>
                {item.invoice_price ? formatPrice(Number(item.invoice_price), 'JPY') : '-'}
              </span>,
            ),
          ]
        : []),
    ];
  });

  return (
    <Layout
      loading={preAlert.isLoading}
      title={type === 'current' ? bookingsStrings.currentBookings : bookingsStrings.pastBookings}
      errors={[preAlert.error]}
      breadcrumbs={[
        '/utilities/Dashboard',
        type === 'current'
          ? '/utilities/Bookings/CurrentBookings'
          : '/utilities/Bookings/PastBookings',
      ]}
    >
      <ExportCSVDialog
        open={UIState.exportDialog.open}
        onClose={() => {
          setUIState((state) => {
            return { ...state, exportDialog: { open: false } };
          });
        }}
        baseUrl={'/pre-alert/'}
        query={queries}
        defaultQuery={{ type: queries.type }}
        selectedIds={checklist}
        subject={strings.booking}
      />
      <BookingsContainer>
        <Filter>
          <Filter.Field name="d2d_no" />
          <Filter.Field name="author" />
          <Filter.Field name="vessel" />
          <div style={{ width: '100%' }} />
          <Filter.Button type="submit" text={strings.search} />
          <Filter.Button type="reset" text={strings.reset} />
          <Filter.Button
            type="button"
            onClick={() => {
              setUIState((state) => {
                return { ...state, exportDialog: { open: true } };
              });
            }}
            text={strings.exportCSV}
          />
        </Filter>

        <Table
          columnFixedUntil={isMobile ? undefined : 4}
          fixedWidths={isMobile ? undefined : [60, 60, 120, 60]}
        >
          <Table.Head>
            <Table.Row columns={headColumns} />
          </Table.Head>
          <Table.Body>
            {bodyRows == null
              ? null
              : bodyRows.map((columns, i) => <Table.Row key={i} columns={columns} />)}
          </Table.Body>
        </Table>

        <div className={clsx(classes.flexInline, classes.justifyEnd)}>
          <PaginationWithSearchQuery query={preAlert} pageSize={pageCount} />
        </div>
      </BookingsContainer>
    </Layout>
  );
};
